import React, { useState, useMemo } from "react";
import { useEditor, Element } from "@craftjs/core";
import { toast } from "react-toastify";
import {
  Container,
  Text,
  Button,
  Image,
  HeroBanner,
  ProductShowcase,
  FeaturesList,
  PromoBanner,
  TestimonialsSection,
  ContactSection,
  FooterSection,
  Divider,
} from "./CraftComponents";
import {
  MdViewQuilt,
  MdTextFields,
  MdSmartButton,
  MdImage,
  MdStar,
  MdLocalOffer,
  MdCheckCircle,
  MdPhone,
  MdWeb,
  MdLayers,
  MdStorefront,
  MdAdd,
  MdSearch,
  MdDragIndicator,
  MdHorizontalRule,
  MdOutlineTouchApp,
  MdOutlineDragIndicator,
} from "react-icons/md";

const ToolboxItem = ({
  id,
  name,
  desc,
  icon: Icon,
  color,
  getComponent,
  onAdd,
}) => {
  return (
    <div
      draggable={true}
      onDragStart={(e) => {
        e.dataTransfer.setData("application/craft-block", id);
        e.dataTransfer.effectAllowed = "copy";
      }}
      className='group relative flex items-start gap-3 p-3 bg-white dark:bg-gray-800/80 hover:bg-indigo-50/70 dark:hover:bg-indigo-950/40 border border-gray-200/80 dark:border-gray-700/60 hover:border-indigo-400 dark:hover:border-indigo-500 rounded-xl cursor-grab active:cursor-grabbing transition-all duration-200 shadow-sm hover:shadow-md select-none'
    >
      {/* Icon Badge */}
      <div
        className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 shadow-sm pointer-events-none ${color}`}
      >
        <Icon size={20} />
      </div>

      {/* Content */}
      <div className='flex-1 min-w-0 pr-12 pointer-events-none'>
        <div className='flex items-center gap-1.5'>
          <h4 className='text-xs font-bold text-gray-900 dark:text-gray-100 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors truncate'>
            {name}
          </h4>
        </div>
        <p className='text-[11px] text-gray-500 dark:text-gray-400 line-clamp-2 mt-0.5 leading-snug'>
          {desc}
        </p>
      </div>

      {/* Action Button: Quick Add Click */}
      <div className='absolute right-2 top-2 bottom-2 flex flex-col items-center justify-between'>
        <button
          type='button'
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onAdd(getComponent(), name);
          }}
          className='p-1.5 bg-indigo-50 text-indigo-600 hover:bg-indigo-600 hover:text-white dark:bg-gray-700 dark:text-indigo-300 dark:hover:bg-indigo-600 dark:hover:text-white rounded-lg shadow-sm transition-all transform hover:scale-110 pointer-events-auto'
          title={`Click to add "${name}" to page`}
        >
          <MdAdd size={16} />
        </button>

        <span
          className='text-gray-400 dark:text-gray-500 group-hover:text-indigo-500 p-0.5 flex items-center pointer-events-none'
          title='Drag and drop onto canvas'
        >
          <MdDragIndicator size={16} />
        </span>
      </div>
    </div>
  );
};

export const CraftToolbox = () => {
  const { actions, query } = useEditor(() => null);
  const [activeTab, setActiveTab] = useState("blocks"); // "blocks" or "elements"
  const [searchQuery, setSearchQuery] = useState("");

  // Store Blocks definitions
  const storeBlocks = useMemo(
    () => [
      {
        id: "hero",
        name: "Hero Banner",
        desc: "High-impact visual banner with headline, CTA & background",
        icon: MdWeb,
        color: "bg-gradient-to-br from-indigo-500 to-purple-600 text-white",
        getComponent: () => <HeroBanner />,
      },
      {
        id: "products",
        name: "Product Showcase",
        desc: "Live product grid with price tags, badges & quick cart",
        icon: MdStorefront,
        color: "bg-gradient-to-br from-emerald-500 to-teal-600 text-white",
        getComponent: () => <ProductShowcase />,
      },
      {
        id: "features",
        name: "Feature Highlights",
        desc: "Fast delivery, secure payments & 24/7 support icons",
        icon: MdCheckCircle,
        color: "bg-gradient-to-br from-blue-500 to-cyan-600 text-white",
        getComponent: () => <FeaturesList />,
      },
      {
        id: "promo",
        name: "Promo / Deal Banner",
        desc: "Eye-catching discount banner with coupon code copy",
        icon: MdLocalOffer,
        color: "bg-gradient-to-br from-rose-500 to-pink-600 text-white",
        getComponent: () => <PromoBanner />,
      },
      {
        id: "testimonials",
        name: "Testimonials",
        desc: "Customer review cards with avatars & 5-star ratings",
        icon: MdStar,
        color: "bg-gradient-to-br from-amber-500 to-orange-600 text-white",
        getComponent: () => <TestimonialsSection />,
      },
      {
        id: "contact",
        name: "Contact & Store Info",
        desc: "Address, phone, email & direct WhatsApp chat button",
        icon: MdPhone,
        color: "bg-gradient-to-br from-teal-500 to-emerald-600 text-white",
        getComponent: () => <ContactSection />,
      },
      {
        id: "footer",
        name: "Store Footer",
        desc: "Store links, policies, customer care & copyright",
        icon: MdLayers,
        color: "bg-gradient-to-br from-slate-700 to-gray-900 text-white",
        getComponent: () => <FooterSection />,
      },
    ],
    [],
  );

  // Basic Elements definitions
  const basicElements = useMemo(
    () => [
      {
        id: "container",
        name: "Container Section",
        desc: "Customizable box wrapper to group & style other elements",
        icon: MdViewQuilt,
        color: "bg-purple-600 text-white",
        getComponent: () => (
          <Element
            is={Container}
            canvas
            background='#f8fafc'
            padding={32}
            borderRadius={16}
            gap={16}
          >
            <Text
              text='New Section Title'
              fontSize={24}
              fontWeight='700'
              color='#111827'
            />
            <Text
              text='Customize this section or drop other elements inside.'
              fontSize={15}
              color='#4b5563'
            />
          </Element>
        ),
      },
      {
        id: "heading",
        name: "Heading / Title",
        desc: "Large bold title for sections or promotions",
        icon: MdTextFields,
        color: "bg-blue-600 text-white",
        getComponent: () => (
          <Text
            text='Your Section Heading'
            fontSize={28}
            fontWeight='800'
            color='#111827'
          />
        ),
      },
      {
        id: "paragraph",
        name: "Paragraph Text",
        desc: "Clean body text block for descriptions and details",
        icon: MdTextFields,
        color: "bg-slate-600 text-white",
        getComponent: () => (
          <Text
            text='Add your engaging product description or story here. We craft top quality items for your daily lifestyle.'
            fontSize={15}
            fontWeight='400'
            color='#374151'
            lineHeight={1.6}
          />
        ),
      },
      {
        id: "button",
        name: "Action Button",
        desc: "Clickable button with link, colors & custom radius",
        icon: MdSmartButton,
        color: "bg-indigo-600 text-white",
        getComponent: () => (
          <Button
            text='Shop Collection'
            backgroundColor='#4f46e5'
            textColor='#ffffff'
          />
        ),
      },
      {
        id: "image",
        name: "Image Box",
        desc: "Responsive image with customizable height & rounded corners",
        icon: MdImage,
        color: "bg-pink-500 text-white",
        getComponent: () => <Image />,
      },
      {
        id: "divider",
        name: "Divider / Spacer",
        desc: "Horizontal line separator or vertical layout spacing",
        icon: MdHorizontalRule,
        color: "bg-gray-600 text-white",
        getComponent: () => <Divider />,
      },
    ],
    [],
  );

  const handleAddElement = React.useCallback(
    (reactElement, elementName, overrideTargetNodeId = null) => {
      try {
        const nodeTree = query.parseReactElement(reactElement).toNodeTree();

        let targetParentId = "ROOT";
        let targetIndex = undefined;

        // Check if a node is currently selected or overridden by a drop
        const selectedNodeId =
          overrideTargetNodeId || query.getEvent("selected").first();
        if (selectedNodeId) {
          try {
            const selectedNode = query.node(selectedNodeId).get();
            if (selectedNode?.data?.isCanvas) {
              // If the selected node is a canvas container, add inside it
              targetParentId = selectedNodeId;
            } else if (selectedNode?.data?.parent) {
              // If selected node is a child, insert right after it
              targetParentId = selectedNode.data.parent;
              const parentNode = query.node(targetParentId).get();
              if (parentNode?.data?.nodes) {
                const idx = parentNode.data.nodes.indexOf(selectedNodeId);
                if (idx !== -1) {
                  targetIndex = idx + 1;
                }
              }
            }
          } catch (e) {
            console.warn("Target node lookup warning:", e);
          }
        }

        actions.addNodeTree(nodeTree, targetParentId, targetIndex);

        // Select newly added root node from nodeTree
        if (nodeTree?.rootNodeId) {
          setTimeout(() => {
            try {
              actions.selectNode(nodeTree.rootNodeId);
            } catch (err) {
              // ignore
            }
          }, 100);
        }

        toast.success(`✨ Added "${elementName}" to page!`);
      } catch (err) {
        console.error("Failed to add component:", err);
        // Fallback: append directly to ROOT canvas
        try {
          const nodeTree = query.parseReactElement(reactElement).toNodeTree();
          actions.addNodeTree(nodeTree, "ROOT");
          toast.success(`✨ Added "${elementName}" to page!`);
        } catch (fallbackErr) {
          console.error("Fallback add failed:", fallbackErr);
          toast.error("Could not add block. Try dragging it into the canvas.");
        }
      }
    },
    [query, actions],
  );

  // Filter items based on search query
  const filteredBlocks = storeBlocks.filter(
    (b) =>
      b.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      b.desc.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const filteredElements = basicElements.filter(
    (e) =>
      e.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      e.desc.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  // Listen for native HTML5 drops from the Canvas
  React.useEffect(() => {
    const handleNativeDrop = (e) => {
      const { blockId, targetNodeId } = e.detail;
      const block = [...storeBlocks, ...basicElements].find(
        (b) => b.id === blockId,
      );
      if (block) {
        handleAddElement(block.getComponent(), block.name, targetNodeId);
      }
    };
    window.addEventListener("craft-html5-drop", handleNativeDrop);
    return () =>
      window.removeEventListener("craft-html5-drop", handleNativeDrop);
  }, [storeBlocks, basicElements, handleAddElement]);

  return (
    <div className='h-full flex flex-col bg-gray-50/50 dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 select-none'>
      {/* Header */}
      <div className='p-4 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 space-y-3'>
        <div className='flex items-center justify-between'>
          <h3 className='text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300 flex items-center gap-1.5'>
            <span className='w-2 h-2 rounded-full bg-indigo-600 inline-block' />
            Component Toolbox
          </h3>
          <span className='text-[10px] bg-indigo-50 text-indigo-700 dark:bg-indigo-950/60 dark:text-indigo-300 font-bold px-2 py-0.5 rounded-full border border-indigo-200/60 dark:border-indigo-800'>
            Drag & Drop / Click
          </span>
        </div>

        {/* Tab switch */}
        <div className='grid grid-cols-2 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl gap-1'>
          <button
            type='button'
            onClick={() => setActiveTab("blocks")}
            className={`py-1.5 px-3 text-xs font-bold rounded-lg transition-all ${
              activeTab === "blocks"
                ? "bg-white dark:bg-gray-700 text-indigo-600 dark:text-indigo-400 shadow-sm"
                : "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
            }`}
          >
            Store Blocks ({storeBlocks.length})
          </button>
          <button
            type='button'
            onClick={() => setActiveTab("elements")}
            className={`py-1.5 px-3 text-xs font-bold rounded-lg transition-all ${
              activeTab === "elements"
                ? "bg-white dark:bg-gray-700 text-indigo-600 dark:text-indigo-400 shadow-sm"
                : "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
            }`}
          >
            Basic Elements ({basicElements.length})
          </button>
        </div>

        {/* Search filter */}
        <div className='relative'>
          <MdSearch
            size={16}
            className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400'
          />
          <input
            type='text'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={`Search ${activeTab === "blocks" ? "store blocks" : "basic elements"}...`}
            className='w-full pl-9 pr-3 py-1.5 bg-gray-100 dark:bg-gray-800/80 border border-transparent focus:border-indigo-500 rounded-xl text-xs text-gray-900 dark:text-white outline-none transition-all placeholder:text-gray-400'
          />
        </div>
      </div>

      {/* Guide Banner */}
      <div className='px-3.5 py-2 bg-indigo-50/60 dark:bg-indigo-950/30 border-b border-indigo-100/60 dark:border-indigo-900/40 flex items-center justify-between text-[11px] text-indigo-900 dark:text-indigo-300'>
        <div className='flex items-center gap-1.5'>
          <MdOutlineDragIndicator size={14} className='text-indigo-600' />
          <span>Drag onto page</span>
        </div>
        <span className='text-indigo-400'>•</span>
        <div className='flex items-center gap-1.5'>
          <MdOutlineTouchApp size={14} className='text-indigo-600' />
          <span>
            Click <strong>+</strong> to add
          </span>
        </div>
      </div>

      {/* Toolbox Items List */}
      <div className='flex-1 overflow-y-auto p-3 space-y-2.5'>
        {activeTab === "blocks" ? (
          filteredBlocks.length > 0 ? (
            filteredBlocks.map((block) => (
              <ToolboxItem
                key={block.id}
                id={block.id}
                name={block.name}
                desc={block.desc}
                icon={block.icon}
                color={block.color}
                getComponent={block.getComponent}
                onAdd={handleAddElement}
              />
            ))
          ) : (
            <div className='p-6 text-center text-xs text-gray-400'>
              No blocks found matching "{searchQuery}"
            </div>
          )
        ) : filteredElements.length > 0 ? (
          filteredElements.map((elem) => (
            <ToolboxItem
              key={elem.id}
              id={elem.id}
              name={elem.name}
              desc={elem.desc}
              icon={elem.icon}
              color={elem.color}
              getComponent={elem.getComponent}
              onAdd={handleAddElement}
            />
          ))
        ) : (
          <div className='p-6 text-center text-xs text-gray-400'>
            No elements found matching "{searchQuery}"
          </div>
        )}
      </div>
    </div>
  );
};
